/**
 * Filtros de la lista de mensajes.
 *
 * Los filtros viven en la query string: asi un enlace a "todo lo fallido de SAP
 * en el flujo F10" se puede pegar en un canal de soporte y el que lo abre ve
 * exactamente lo mismo. El filtrado real lo hace la API (el message log es
 * grande); `matchesQuickSearch` solo refina lo que ya esta cargado en pantalla.
 */
import type { MessageLogEntry } from '../api/contracts';
import type { IncidentFilters, MessageFilters, PageState, RunFilters } from '../api/types';
import {
  DIRECTION_OPTIONS,
  FLOW_OPTIONS,
  RUN_STATUS_OPTIONS,
  STATUS_OPTIONS,
  SYSTEM_OPTIONS,
  type Option,
} from './catalogs';
import { datetimeLocalToIso } from './format';

export const DEFAULT_PAGE_SIZE = 50;

export const PAGE_SIZE_OPTIONS: readonly number[] = [25, 50, 100, 200];

export const EMPTY_MESSAGE_FILTERS: MessageFilters = {
  system: null,
  status: null,
  flow: null,
  direction: null,
  correlationId: '',
  eventType: '',
  from: '',
  to: '',
};

export const EMPTY_INCIDENT_FILTERS: IncidentFilters = {
  system: null,
  flow: null,
  resolution: null,
};

/** Acepta un valor de la query string solo si es una opcion conocida. */
function parseOption<T extends string>(
  options: readonly Option<T>[],
  raw: string | null,
): T | null {
  if (!raw) {
    return null;
  }
  const found = options.find((option) => option.value === raw);
  return found ? found.value : null;
}

/** Reconstruye los filtros desde la URL. Lo que no se entiende se ignora. */
export function messageFiltersFromSearchParams(params: URLSearchParams): MessageFilters {
  return {
    system: parseOption(SYSTEM_OPTIONS, params.get('system')),
    status: parseOption(STATUS_OPTIONS, params.get('status')),
    flow: parseOption(FLOW_OPTIONS, params.get('flow')),
    direction: parseOption(DIRECTION_OPTIONS, params.get('direction')),
    correlationId: params.get('correlationId') ?? '',
    eventType: params.get('eventType') ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
  };
}

export function pageStateFromSearchParams(params: URLSearchParams): PageState {
  const page = Number.parseInt(params.get('page') ?? '', 10);
  const pageSize = Number.parseInt(params.get('pageSize') ?? '', 10);
  return {
    page: Number.isFinite(page) && page > 0 ? page : 1,
    pageSize: PAGE_SIZE_OPTIONS.includes(pageSize) ? pageSize : DEFAULT_PAGE_SIZE,
  };
}

/** Serializa filtros + paginado a la query string de la UI (omite lo vacio). */
export function messageFiltersToSearchParams(
  filters: MessageFilters,
  page: PageState,
): URLSearchParams {
  const params = new URLSearchParams();
  const put = (key: string, value: string | null): void => {
    if (value !== null && value !== '') {
      params.set(key, value);
    }
  };
  put('system', filters.system);
  put('status', filters.status);
  put('flow', filters.flow);
  put('direction', filters.direction);
  put('correlationId', filters.correlationId.trim());
  put('eventType', filters.eventType.trim());
  put('from', filters.from);
  put('to', filters.to);
  if (page.page > 1) {
    params.set('page', String(page.page));
  }
  if (page.pageSize !== DEFAULT_PAGE_SIZE) {
    params.set('pageSize', String(page.pageSize));
  }
  return params;
}

/**
 * Query que se manda a la API. Las fechas se normalizan a ISO UTC: la UI trabaja
 * con hora local y el backend siempre con UTC.
 */
export function messageFiltersToQuery(
  filters: MessageFilters,
  page: PageState,
): Record<string, string> {
  const query: Record<string, string> = {
    page: String(page.page),
    pageSize: String(page.pageSize),
  };
  const put = (key: string, value: string | null): void => {
    if (value !== null && value !== '') {
      query[key] = value;
    }
  };
  put('system', filters.system);
  put('status', filters.status);
  put('flow', filters.flow);
  put('direction', filters.direction);
  put('correlationId', filters.correlationId.trim());
  put('eventType', filters.eventType.trim());
  put('from', filters.from ? datetimeLocalToIso(filters.from) : null);
  put('to', filters.to ? datetimeLocalToIso(filters.to) : null);
  return query;
}

export function incidentFiltersToQuery(
  filters: IncidentFilters,
  page: PageState,
): Record<string, string> {
  const query: Record<string, string> = {
    page: String(page.page),
    pageSize: String(page.pageSize),
  };
  if (filters.system) {
    query['system'] = filters.system;
  }
  if (filters.flow) {
    query['flow'] = filters.flow;
  }
  if (filters.resolution) {
    query['resolution'] = filters.resolution;
  }
  return query;
}

export const EMPTY_RUN_FILTERS: RunFilters = {
  flow: null,
  status: null,
  correlationId: '',
  from: '',
  to: '',
};

/**
 * Filtros de corridas desde la URL. El flujo va en `flujo`, igual que en
 * Procesos, para que "ver corridas de F8" sea el mismo parametro en las dos.
 */
export function runFiltersFromSearchParams(params: URLSearchParams): RunFilters {
  return {
    flow: parseOption(FLOW_OPTIONS, params.get('flujo')),
    status: parseOption(RUN_STATUS_OPTIONS, params.get('status')),
    correlationId: params.get('correlationId') ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
  };
}

export function runFiltersToSearchParams(filters: RunFilters, page: PageState): URLSearchParams {
  const params = new URLSearchParams();
  const put = (key: string, value: string | null): void => {
    if (value !== null && value !== '') {
      params.set(key, value);
    }
  };
  put('flujo', filters.flow);
  put('status', filters.status);
  put('correlationId', filters.correlationId.trim());
  put('from', filters.from);
  put('to', filters.to);
  if (page.page > 1) {
    params.set('page', String(page.page));
  }
  if (page.pageSize !== DEFAULT_PAGE_SIZE) {
    params.set('pageSize', String(page.pageSize));
  }
  return params;
}

/** Query de corridas para la API: fechas a ISO UTC, como en mensajes. */
export function runFiltersToQuery(filters: RunFilters, page: PageState): Record<string, string> {
  const query: Record<string, string> = {
    page: String(page.page),
    pageSize: String(page.pageSize),
  };
  const put = (key: string, value: string | null): void => {
    if (value !== null && value !== '') {
      query[key] = value;
    }
  };
  put('flow', filters.flow);
  put('status', filters.status);
  put('correlationId', filters.correlationId.trim());
  put('from', filters.from ? datetimeLocalToIso(filters.from) : null);
  put('to', filters.to ? datetimeLocalToIso(filters.to) : null);
  return query;
}

export function activeRunFilterCount(filters: RunFilters): number {
  const values: readonly (string | null)[] = [
    filters.flow,
    filters.status,
    filters.correlationId.trim(),
    filters.from,
    filters.to,
  ];
  return values.filter((value) => value !== null && value !== '').length;
}

/** Cuantos filtros estan aplicados (para el contador del boton "Limpiar"). */
export function activeMessageFilterCount(filters: MessageFilters): number {
  const values: readonly (string | null)[] = [
    filters.system,
    filters.status,
    filters.flow,
    filters.direction,
    filters.correlationId.trim(),
    filters.eventType.trim(),
    filters.from,
    filters.to,
  ];
  return values.filter((value) => value !== null && value !== '').length;
}

export function hasActiveMessageFilters(filters: MessageFilters): boolean {
  return activeMessageFilterCount(filters) > 0;
}

/**
 * Refino local sobre la pagina ya cargada. Busca en los campos que uno mira
 * cuando esta persiguiendo un problema: correlation id, evento, clave de
 * idempotencia y el ultimo error.
 */
export function matchesQuickSearch(entry: MessageLogEntry, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (needle === '') {
    return true;
  }
  const haystack: readonly (string | null | undefined)[] = [
    entry.id,
    entry.correlationId,
    entry.eventType,
    entry.idempotencyKey,
    entry.lastError,
    entry.system,
    entry.status,
    entry.flow,
  ];
  return haystack.some(
    (value) => typeof value === 'string' && value.toLowerCase().includes(needle),
  );
}
